import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Formsy from 'formsy-react';
import { withRouter } from 'react-router-dom';
import { Button, FormControl, Divider, InputAdornment, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import Region from '../../constants/region';
import { getRole, getUser, getUserRegion } from '../../../utils';
import { LocalizationConsumer } from '../../localization/LocalizationContext';
import { TextFieldFormsy } from '@fuse';
import { SelectSearchFormsy } from '../../../@fuse/components/formsy';
import SearchableSelectAll from '../../custom-widgets/SearchableSelectAll';
import LocalizedDatePicker from '../../custom-widgets/LocalizedDatePicker';
import listOfStates from 'us-state-converter';
import { useCreateTrailer, useUpdateTrailer } from '../../data/generic-queries';
import { NotificationContext } from '../../NotificationContext';
import { useHistory } from 'react-router';
import useFetchHaulerYards from '../../main/apps/FunctionalApps/user-profile/hauler-profile/components/yards/HaulerYards.queries';
import { SearchableSelect } from '../../custom-widgets';
import Split from '../../split';
import generateErrorMessage from '../../repository/utils/generateErrorMessage';

const getSaveErrorMessage = (error, trailer) => {
    const { message } = generateErrorMessage(error, 'save', 'trailer');
    if (!message.includes('E11000')) return message;
    if (message.includes('barcode')) return 'A trailer with this barcode already exists.';
    return `A trailer with number ${trailer.trailer_number} already exists.`;
};

const CreateTrailerForm = ({ editData, defaultHauler, haulingCompanies }) => {
    const region = getUserRegion();
    const role = getRole();
    const history = useHistory();
    const [canSubmit, setCanSubmit] = useState(false);
    const [compartment1, setCompartment1] = useState(0);
    const [compartment2, setCompartment2] = useState(0);
    const [compartment3, setCompartment3] = useState(0);
    const [hauler, setHauler] = useState(defaultHauler ? { name: defaultHauler.name, value: defaultHauler.id } : null);
    const [selectedHaulerId, setSelectedHaulerId] = useState(null);
    const [haulerYardOptions, setHaulerYardOptions] = useState([]);
    const [haulerYard, setHaulerYard] = useState({ value: null, name: 'None' });
    const [expiryDate, setExpiryDate] = useState();
    const [expiryDateError, setExpiryDateError] = useState();
    const { mutateAsync: updateTrailer } = useUpdateTrailer({ key: 'trailer-data' });
    const { mutateAsync: createTrailer } = useCreateTrailer({ key: 'trailer-data' });
    const { showSnackbar } = useContext(NotificationContext);

    const showHaulerYardsField = Split.getBooleanTreatment('enable_hauler_profile');

    const { data: haulingYards } = useFetchHaulerYards({
        enabled: !!selectedHaulerId,
        hauler_id: selectedHaulerId,
    });

    useEffect(() => {
        if (!!editData) {
            setCompartment1(editData.compartment1 === 0 ? 0 : editData.compartment1);
            setCompartment2(editData.compartment2 === 0 ? 0 : editData.compartment2);
            setCompartment3(editData.compartment3 === 0 ? 0 : editData.compartment3);
        } else if (role === 'transport') {
            const { id, name, subuser, hauling_id } = getUser();

            if (subuser) {
                setHauler(haulingCompanies.find((entry) => entry.value === hauling_id));
            } else setHauler({ name, value: id });
        }
    }, [editData, region, role, haulingCompanies]);

    // Sanitize and set selectedHaulerId when hauler changes
    useEffect(() => {
        const sanitizedHaulerId = hauler?.value && hauler.value !== ' ' && hauler.value !== null ? hauler.value : null;
        const previousHaulerId = selectedHaulerId;
        setSelectedHaulerId(sanitizedHaulerId);

        // Reset hauler yard when hauler changes
        if (previousHaulerId !== sanitizedHaulerId) {
            setHaulerYard({ value: null, name: 'None' });
        }
    }, [hauler?.value, selectedHaulerId]);

    useEffect(() => {
        if (haulingYards) {
            const options = haulingYards.map((yard) => ({
                value: yard._id,
                name: yard.name,
            }));
            setHaulerYardOptions(options);

            // Set hauler yard from editData if we're editing and haven't set it yet
            if (editData && editData.hauler_yard_id && haulerYard.value === null) {
                const selectedYard = options.find((yard) => yard.value === editData.hauler_yard_id);
                if (selectedYard) {
                    setHaulerYard({ value: selectedYard.value, name: selectedYard.name });
                }
            }
        } else {
            setHaulerYardOptions([]);
        }
    }, [haulingYards, editData]);

    const handleHauler = (event, value) => {
        setHauler(value);
    };

    const handleHaulerYard = (event, value) => {
        setHaulerYard(value);
    };

    const disableButton = () => {
        setCanSubmit(false);
    };

    const enableButton = () => {
        setCanSubmit(true);
    };

    const handleExpiryDate = (newExpiryDate) => {
        setExpiryDate(newExpiryDate);
    };

    const expiryDateWindow = useMemo(() => {
        const expiryDateWindows = {
            [Region.CDI]: {
                value: 2,
                unit: 'years',
            },
            default: null,
        };

        const newExpiryDateWindow = expiryDateWindows[region] || expiryDateWindows.default;

        return newExpiryDateWindow
            ? {
                  minDate: moment().startOf('day'),
                  maxDate: moment().add(newExpiryDateWindow.value, newExpiryDateWindow.unit),
                  value: newExpiryDateWindow.value,
                  unit: newExpiryDateWindow.unit,
              }
            : null;
    }, [region]);

    const onChangeCompartment = (event) => {
        if (event.target.name === 'compartment1') {
            setCompartment1(event.target.value);
        } else if (event.target.name === 'compartment2') {
            setCompartment2(event.target.value);
        } else {
            setCompartment3(event.target.value);
        }
    };

    const onSubmit = async (model) => {
        const submitModel = { ...model };
        submitModel.compartment1 = parseInt(submitModel.compartment1.toString());
        submitModel.compartment2 = parseInt(submitModel.compartment2.toString());
        submitModel.compartment3 = parseInt(submitModel.compartment3.toString());
        submitModel.amount_a = submitModel.compartment1;
        submitModel.amount_b = submitModel.compartment2;
        submitModel.amount_c = submitModel.compartment3;
        submitModel.capacity = parseInt(submitModel.capacity.toString());
        submitModel.hauling_id = selectedHaulerId;
        submitModel.hauler_yard_id = haulerYard?.value || null;
        submitModel.expiry_date = expiryDate;
        if (submitModel.barcode === '') submitModel.barcode = null;

        if (region === Region.UDA) {
            submitModel.empty_weight = submitModel.empty_weight ? parseFloat(submitModel.empty_weight.toString()) : 0;
        }

        try {
            if (editData) {
                await updateTrailer({ id: editData.id, data: submitModel });
                showSnackbar({ message: 'Successfully Edited Trailer.', severity: 'success' });
            } else {
                await createTrailer({ data: submitModel });
                showSnackbar({ message: 'Successfully Created Trailer.', severity: 'success' });
            }
        } catch (error) {
            showSnackbar({ message: getSaveErrorMessage(error, submitModel), severity: 'error' });
            return;
        }

        history.replace({ pathname: '/list-trailers' });
    };

    return (
        <LocalizationConsumer>
            {(localization) => (
                <div className="min-w-3/4 max-w-3/4">
                    <Formsy
                        onValidSubmit={onSubmit}
                        onValid={enableButton}
                        onInvalid={disableButton}
                        className="flex flex-col justify-center"
                        // wrap
                    >
                        <Stack spacing={2} sx={{ pb: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            <SearchableSelectAll
                                options={haulingCompanies.map((entry) => ({ value: entry.value, name: entry.name }))}
                                value={hauler}
                                onChange={handleHauler}
                                label={`${localization.general.hauling_singular}`}
                                disabled={!(role === 'admin' || role === 'sub-admin')}
                                required
                                variant="outlined" // Wrap
                            />

                            {showHaulerYardsField && (
                                <SearchableSelect
                                    options={[{ value: null, name: 'None' }, ...haulerYardOptions]}
                                    value={haulerYard}
                                    onChange={handleHaulerYard}
                                    label="Hauler Yard"
                                    disabled={!(role === 'admin' || role === 'sub-admin') || !selectedHaulerId}
                                    variant="outlined" // Wrap
                                />
                            )}

                            <TextFieldFormsy
                                type="text"
                                name="trailer_number"
                                label={`${localization.general.trailer} Number`}
                                value={editData ? editData.trailer_number : ''}
                                validations="minLength:1"
                                validationError={`This is not a valid ${localization.general.trailer} number`}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                local_shipping
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                required
                                variant="outlined"
                            />

                            <SelectSearchFormsy
                                name="type"
                                label={`${localization.general.trailer} Type`}
                                variant="outlined"
                                options={[
                                    { value: 'Tridem', name: 'Tridem' },
                                    { value: 'Tri Axel', name: 'Tri Axel' },
                                    { value: 'Quad', name: 'Quad' },
                                    { value: 'B-Train', name: 'B-Train' },
                                    { value: 'Super', name: 'Super' },
                                    { value: 'Regular', name: 'Regular' },
                                ]}
                                value={editData ? editData.type : ' '}
                                validations="minLength:2"
                                validationError="Please select a type"
                            />

                            <SelectSearchFormsy
                                className="my-16"
                                variant="outlined"
                                name="is_active"
                                label="Status"
                                options={[
                                    { value: true, name: 'Active' },
                                    { value: false, name: 'Inactive' },
                                ]}
                                value={editData?.is_active || true}
                                // Wrap
                            />

                            <TextFieldFormsy
                                type="text"
                                name="compartment1"
                                label="Front Compartment Capacity"
                                onChange={onChangeCompartment}
                                value={editData ? (editData.compartment1 === 0 ? '0' : editData.compartment1) : 0}
                                validations={{ isNumeric: true, isInt: true }}
                                validationError="This is not a valid number"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                local_drink
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                required
                                variant="outlined"
                            />

                            <TextFieldFormsy
                                type="text"
                                name="compartment2"
                                label="Middle Compartment Capacity (please input 0 if not applicable)"
                                onChange={onChangeCompartment}
                                value={editData ? (editData.compartment2 === 0 ? '0' : editData.compartment2) : 0}
                                validations={{ isNumeric: true, isInt: true }}
                                validationError="This is not a valid number"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                local_drink
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                required
                            />

                            <TextFieldFormsy
                                type="text"
                                name="compartment3"
                                label="Back Compartment Capacity (please input 0 if not applicable)"
                                onChange={onChangeCompartment}
                                value={editData ? (editData.compartment3 === 0 ? '0' : editData.compartment3) : 0}
                                validations={{ isNumeric: true, isInt: true }}
                                validationError="This is not a valid number"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                local_drink
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                required
                            />

                            <TextFieldFormsy
                                type="text"
                                name="capacity"
                                label="Capacity"
                                value={parseInt(compartment1.toString()) + parseInt(compartment2.toString()) + parseInt(compartment3.toString()) || 0}
                                validations={{ isNumeric: true, isInt: true, matchRegexp: /^(?!0+$)[0-9]{1,10}$/ }}
                                validationError="This is not a valid amount for trailer capacity"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                local_drink
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                disabled
                                required
                            />

                            {region === Region.UDA && (
                                <TextFieldFormsy
                                    type="number"
                                    name="empty_weight"
                                    label="Empty Weight"
                                    value={editData ? editData.empty_weight : ''}
                                    validations={{ isNumeric: true }}
                                    validationError="This is not a valid number"
                                    variant="outlined" //
                                />
                            )}

                            <FormControl fullWidth>
                                <LocalizedDatePicker
                                    Picker={DatePicker}
                                    name="expiry_date"
                                    label="Expiry Date"
                                    value={editData?.expiry_date ? moment(editData.expiry_date) : null}
                                    onError={(error) => {
                                        if (error === 'maxDate' || error === 'minDate') {
                                            if (expiryDateWindow) {
                                                const { unit, value } = expiryDateWindow;
                                                setExpiryDateError(`Expiry date must be within ${value} ${unit} of today.`);
                                            } else {
                                                setExpiryDateError(`Expiry date is outside range: ${error}.`);
                                            }
                                            disableButton();
                                        } else if (error === 'invalidDate') {
                                            setExpiryDateError('Please enter a valid date');
                                            disableButton();
                                        } else {
                                            setExpiryDateError(undefined);
                                            enableButton();
                                        }
                                    }}
                                    maxDate={expiryDateWindow?.maxDate ?? null}
                                    minDate={expiryDateWindow?.minDate ?? null}
                                    slotProps={{
                                        textField: {
                                            helperText: expiryDateError,
                                        },
                                    }}
                                    onChange={handleExpiryDate}
                                    InputLabelProps={{ shrink: true }}
                                    // wrap
                                />
                            </FormControl>

                            {region === Region.CDI && (
                                <SelectSearchFormsy
                                    className="my-16"
                                    variant="outlined"
                                    name="permit_state"
                                    label="State of AG - Permit"
                                    options={[{ value: ' ', name: 'None' }, ...listOfStates().map((entry) => ({ value: entry.usps, name: entry.name }))]}
                                    value={editData?.permit_state || ' '}
                                    // Wrap
                                />
                            )}

                            <TextFieldFormsy
                                type="text"
                                name="barcode"
                                label="Barcode Value"
                                placeholder="Barcode Value"
                                value={editData ? editData.barcode : ''}
                                validations={{ minLength: 10, isAlphanumeric: true }}
                                validationError="Unique barcodes must be at least 10 letters or numbers"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="text-20 material-icons" color="action">
                                                list_alt
                                            </i>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                aria-label={editData ? 'EDIT TRAILER' : 'ADD TRAILER'}
                                disabled={!canSubmit || !selectedHaulerId} // Wrap
                            >
                                {`${editData ? 'Edit' : 'Add'} ${localization.general.trailer}`}
                            </Button>
                        </Stack>
                    </Formsy>
                </div>
            )}
        </LocalizationConsumer>
    );
};

export default withRouter(CreateTrailerForm);
